import {
  get,
  post,
  del,
  type ApiError,
} from "@/shared/utils/fetchApi";

export interface StorageChunk {
  id: string;
  content?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata?: Record<string, any>;
  inserted_at?: string;
  version?: number | string;
}

export interface StorageChunksResponse {
  success: boolean;
  data: StorageChunk[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * Lista os chunks (embeddings) associados a um item de storage. Usado pela aba
 * "Chunks" do modal de detalhes (super-admin).
 * - v2: GET /knowledge-bases/:id/storage/:itemId/chunks (envelope flat, sem success)
 * - legacy: GET /knowledge-base/storage/:kbId/:id/chunks ({ success, data, ... })
 */
export async function getStorageItemChunks(
  knowledgeBaseId: string,
  id: string,
  page = 1,
  pageSize = 20,
): Promise<StorageChunksResponse> {
  const qs = `page=${page}&pageSize=${pageSize}`;

  const raw = await get<Omit<StorageChunksResponse, "success">>(
    `/knowledge-bases/${knowledgeBaseId}/storage/${id}/chunks?${qs}`,
  );
  return { success: true, ...raw };
}

// Interfaces para o serviço de storage no domínio Knowledge
export interface KnowledgeUploadResponse {
  id: string;
  url: string;
  name: string;
  size: number;
  type: string;
}

// v2 returns StorageDto (snake_case file_url/file_size). Normalize inside the v2
// branch to keep the legacy-shaped public type intact (no UI churn).
interface StorageDto {
  id: string;
  name: string;
  file_url: string | null;
  file_size: number | null;
  file_type: string | null;
  [key: string]: unknown;
}

export interface KnowledgeUploadConfig {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export async function uploadKnowledgeFile(params: {
  file: File;
  name: string;
  description?: string;
  knowledgeBaseId: string;
  type?: string;
  category: string;
  nodeId?: string;
  toolLogoUrl?: string;
  productId?: string;
  config?: KnowledgeUploadConfig;
  /**
   * PBI 79328 — atributos de negócio do documento (source, subject, language,
   * tags, …). Persistido em `storage.metadata` e replicado em
   * `embeddings.metadata`. Quando ausente, a row fica com metadata=null.
   */
  metadata?: Record<string, unknown>;
  onProgress?: (progress: number) => void;
}) {
  // Fora do try porque o catch também cancela: um upload que falha antes de um
  // segundo ainda pulava para 90% depois de já ter dado erro.
  let progressTimer: ReturnType<typeof setTimeout> | undefined;
  try {
    const formData = new FormData();
    formData.append("file", params.file);
    formData.append("name", params.name);
    formData.append("category", params.category);

    // v2 valida snake_case via vine; legacy aceita camelCase. Snake_case funciona
    // em ambos (Adonis vine + repo legacy parsing) — bridge naming durante cutover.
    formData.append("knowledge_base_id", params.knowledgeBaseId);
    formData.append("knowledgeBaseId", params.knowledgeBaseId);

    if (params.description) {
      formData.append("description", params.description);
    }

    if (params.type) {
      formData.append("type", params.type);
    }

    if (params.nodeId) {
      formData.append("node_id", params.nodeId);
    }

    if (params.toolLogoUrl) {
      formData.append("tool_logo_url", params.toolLogoUrl);
    }

    if (params.productId) {
      formData.append("product_id", params.productId);
    }

    if (params.config) {
      formData.append("config", JSON.stringify(params.config));
    }

    // Atributos de negócio do documento
    if (params.metadata && Object.keys(params.metadata).length > 0) {
      formData.append("metadata", JSON.stringify(params.metadata));
    }

    // Simular progresso para o usuário durante o upload
    //
    // O timer é cancelado antes do 100%: um upload que terminava em menos de um
    // segundo chegava a 100% e depois voltava para 90% quando ele disparava.
    if (params.onProgress) {
      params.onProgress(50);

      // Atualizar para 90% após 1 segundo para dar feedback ao usuário
      progressTimer = setTimeout(() => {
        // Verificar novamente se onProgress existe para evitar erros
        if (params.onProgress) {
          params.onProgress(90);
        }
      }, 1000);
    }

    // Pelo `post` do fetchApi, e não por `fetch` cru: multipart funciona igual
    // (o helper não define Content-Type quando o body é FormData, então o
    // navegador põe o boundary), e o upload passa a herdar o que o `fetch` cru
    // não tinha — refresh de token no 401 e o header `X-Organization-Id`, cuja
    // ausência fazia o upload cair na organização do token em vez da ativa.
    let rawResponse: unknown;
    try {
      rawResponse = await post<unknown>("/storage/items/upload", formData);
    } catch (uploadError) {
      const apiError = uploadError as ApiError;
      // Sentinela consumida pelo caller: 403 FORBIDDEN é falta de assinatura.
      if (apiError?.code === "FORBIDDEN" && apiError?.status === 403) {
        throw new Error("SUBSCRIPTION_REQUIRED");
      }
      throw uploadError;
    }

    // v2 retorna StorageDto flat (snake_case). Normalize pra shape legacy
    // pública (id/url/name/size/type) — UI consumers não precisam mudar.
    const response: KnowledgeUploadResponse = (() => {
      const dto = rawResponse as StorageDto;
      return {
        id: dto.id,
        url: dto.file_url ?? "",
        name: dto.name,
        size: dto.file_size ?? 0,
        type: dto.file_type ?? "",
      };
    })();

    if (progressTimer) {
      clearTimeout(progressTimer);
    }
    if (params.onProgress) {
      params.onProgress(100);
    }

    return response;
  } catch (error) {
    if (progressTimer) {
      clearTimeout(progressTimer);
    }
    console.error("[storage-service] Error uploading file:", error);
    throw error;
  }
}

export async function deleteKnowledgeFile(fileId: string): Promise<void> {
  try {
    // 204 No Content — fetchApi v2 já trata como undefined.
    await del(`/storage/items/${fileId}/file`);
    return;
  } catch (error) {
    console.error("Error deleting file:", error);
    throw error;
  }
}

export async function downloadKnowledgeFile(fileId: string): Promise<void> {
  // Sem guarda de token aqui: ela curto-circuitava ANTES do fetchApi e matava o
  // refresh no 401 — quem tinha access token expirado mas refresh válido não
  // recuperava, e o download falhava sem tentar. Resquício de quando esta chamada
  // era `fetch` cru. O 401 sem sessão recuperável continua vindo da api, com o
  // mesmo `code` que a UI traduz.
  // `parseJson: false` devolve o `Response` cru, que é o que o download precisa
  // (blob + Content-Disposition), e ainda passa pelo fetchApi: refresh de token
  // no 401 e header `X-Organization-Id`, que o `fetch` cru não mandava. O erro
  // já sobe como ApiError, então não há mais `throwFromResponse` aqui.
  const response = await get<Response>(`/storage/items/${fileId}/download`, {
    parseJson: false,
  });

  const contentDisposition = response.headers.get("Content-Disposition");
  let fileName = "download";

  if (contentDisposition) {
    const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);

    if (utf8Match?.[1]) {
      fileName = decodeURIComponent(utf8Match[1].trim());
    } else {
      const standardMatch = contentDisposition.match(/filename="([^"]+)"/);

      if (standardMatch?.[1]) {
        fileName = standardMatch[1];
      }
    }
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);

  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    window.URL.revokeObjectURL(url);
  }
}
